import React, { Fragment, useMemo, useState } from 'react';
import AppTable from '../../../../constants/AppTable';
import { LoadingSpinner } from '../../../../constants/AppLoading';
import { PostalReceive } from '../../../../helpers/models/Models';
import { useFormik } from 'formik';
import { putDataToTheServer } from '../../../../helpers/api';
import { ToastSuccess, ToastWarning } from '../../../../constants/Toast';
import AppURL from '../../../../constants/AppURL';
import DialogBox from '../../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../../constants/InputTextBox';
import FileTextBox from '../../../../constants/FileTextBox';
import { EndBaseLine } from '../../../../constants/AppSpace';
import { Button } from '../../../../constants/Button';

interface PostalReceiveListViewProps {
  data: {
    results: PostalReceive[];
    previous: string | null;
    next: string | null;
  } | null;
  loading: boolean;
  error: string | null;
  setOnURL: React.Dispatch<React.SetStateAction<string>>;
  handleFormSuccess: () => void;

}

const PostalListView: React.FC<PostalReceiveListViewProps> = ({ data, loading, handleFormSuccess, error, setOnURL }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<PostalReceive | null>(null);

  const columns = useMemo(() => [
    { header: 'SN', accessor: 'sn', sortable: true },
    { header: 'Reference no', accessor: 'reference_no', sortable: true },
    { header: 'From Title', accessor: 'from_title', sortable: true },
    { header: 'To Title', accessor: 'to_title', sortable: false },
    { header: 'Date(BS)', accessor: 'date_ne', sortable: true },
    { header: 'Letter No', accessor: 'letter_no', sortable: false },
    { header: 'Address', accessor: 'address', sortable: false },
    { header: 'Actions', accessor: 'actions', action: true },
  ], []);

  const modifiedData = useMemo(() => {
    if (!data) return [];
    return data.results.map((item: PostalReceive) => ({
      sn: item.sn,
      reference_no: item.reference_no,
      from_title: item.from_title,
      to_title: item.to_title,
      date_ne: item.date_ne,
      letter_no: item.letter_no,
      address: item.address,
      created_at: item.created_at,
    }));
  }, [data]);

  const formik = useFormik({
    initialValues: {
      reference_no: currentItem?.reference_no || "",
      from_title: currentItem?.from_title || "",
      address: currentItem?.address || "",
      note: currentItem?.note || "",
      to_title: currentItem?.to_title || "",
      letter_no: currentItem?.letter_no || "",
      doc: currentItem?.doc || "",
    },
    enableReinitialize: true,
    onSubmit: async (values, action) => {
      const formData = new FormData();
      formData.append("reference_no", values.reference_no);
      formData.append("from_title", values.from_title);
      formData.append("address", values.address);
      formData.append("note", values.note);
      formData.append("to_title", values.to_title);
      formData.append("letter_no", values.letter_no);
      if(values.doc){
        formData.append("doc", values.doc);
      }
      try {
        await putDataToTheServer(AppURL.ApplicationURL + 'app/postal/receive/retrieve/update/view/' + currentItem?.sn, formData).then((res) => {
          if (res?.status === 200) {
            handleFormSuccess();
            setDialogOpen(false);
            ToastSuccess({ message: res?.message });
          } else if (res?.status === 400) {
            ToastWarning({ message: res?.message?.tole_name });
          }
          else {
            ToastWarning({ message: "Some Issue" });
          }
        });
      } catch (error) {
        console.log(error);
      }
    },
  });

  const handleEdit = (item: PostalReceive) => {
    setCurrentItem(item);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <p>Error loading data: {error}</p>;
  }

  return (
    <Fragment>
      <AppTable
        columns={columns}
        data={modifiedData}
        onNext={() => data?.next && setOnURL(data.next)}
        onPrev={() => data?.previous && setOnURL(data.previous)}
        onEdit={handleEdit}
      />
      {dialogOpen && currentItem && (
        <DialogBox open={dialogOpen} headerName='Tole Edit Update' close={() => setDialogOpen(false)}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container columnSpacing={3} rowSpacing={0.8}>
              <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                <InputTextBox
                  type='text'
                  name='reference_no'
                  label='Reference No'
                  handleChange={formik.handleChange}
                  value={formik.values.reference_no}
                  error={formik.errors.reference_no}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.reference_no}
                  required={true}
                  placeholder='Enter Reference No.'
                />
              </Grid>
              <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                <InputTextBox
                  type='text'
                  name='from_title'
                  label='From Title'
                  handleChange={formik.handleChange}
                  value={formik.values.from_title}
                  error={formik.errors.from_title}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.from_title}
                  required={true}
                  placeholder='Enter From title'
                />
              </Grid>
              <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                <InputTextBox
                  type='text'
                  name='address'
                  label='Address'
                  handleChange={formik.handleChange}
                  value={formik.values.address}
                  error={formik.errors.address}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.address}
                  required={false}
                  placeholder='Enter address'
                />
              </Grid>
              <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                <InputTextBox
                  type='text'
                  name='note'
                  label='Note'
                  handleChange={formik.handleChange}
                  value={formik.values.note}
                  error={formik.errors.note}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.note}
                  required={false}
                  placeholder='Enter Notes'
                />
              </Grid>
              <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                <InputTextBox
                  type='text'
                  name='to_title'
                  label='To title'
                  handleChange={formik.handleChange}
                  value={formik.values.to_title}
                  error={formik.errors.to_title}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.to_title}
                  required={false}
                  placeholder='Enter To title'
                />
              </Grid>
              <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                <InputTextBox
                  type='text'
                  name='letter_no'
                  label='Letter No.'
                  handleChange={formik.handleChange}
                  value={formik.values.letter_no}
                  error={formik.errors.letter_no}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.letter_no}
                  required={false}
                  placeholder='Enter Letter No.'
                />
              </Grid>
              <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                  <FileTextBox
                    type='file'
                    name='doc'
                    label='Docs'
                    handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        formik.setFieldValue("doc", file); // Handle the file change correctly
                      }
                    }}
                    error={formik.errors.doc}
                    handleBlur={formik.handleBlur}
                    touched={formik.touched.doc}
                    required={false}
                  />
                </Grid>
              </Grid>
              <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                <EndBaseLine top={18}>
                  <Button btn_type='update' type='submit' el='button' />
                </EndBaseLine>
              </Grid>
            </Grid>
          </form>
        </DialogBox>
      )}
    </Fragment>
  );
};

export default PostalListView;
